import {
  findSidebarGroup,
  getAllSidebarPermissionPageIds,
  getSidebarPermissionSections,
} from "@/common/Sidebar/sidebarNavConfig";
import type { ApiEmployeeRole } from "./employeeRoleTypes";

export interface PageAccess {
  view: boolean;
  edit: boolean;
}

export interface PlatformPowerDefinition {
  id: string;
  label: string;
}

export interface RolePermissionsConfig {
  /** Super Admin — full platform access; page matrix is not used. */
  isFullAccess: boolean;
  pagePermissions: Record<string, PageAccess>;
  platformPowers: Record<string, boolean>;
}

export type ResolvedRolePermissions = RolePermissionsConfig;

export const PLATFORM_POWERS: PlatformPowerDefinition[] = [
  { id: "impersonate-users", label: "Impersonate Users" },
  { id: "purge-employees", label: "Purge Employees" },
  { id: "manage-employee-roles", label: "Manage Employee Roles" },
  { id: "manage-employee-departments", label: "Manage Employee Departments" },
  { id: "manage-platform-settings", label: "Manage Platform Settings" },
  { id: "manage-governance-configuration", label: "Manage Governance Configuration" },
  { id: "view-audit-logs", label: "View Audit Logs" },
  { id: "export-data", label: "Export Data" },
];

/** @deprecated Use PLATFORM_POWERS — legacy alias from earlier UI iteration. */
export const SPECIAL_PERMISSIONS = PLATFORM_POWERS;

function emptyPagePermissions(): Record<string, PageAccess> {
  return Object.fromEntries(
    getAllSidebarPermissionPageIds().map((pageId) => [pageId, { view: false, edit: false }]),
  );
}

function emptyPlatformPowers(): Record<string, boolean> {
  return Object.fromEntries(PLATFORM_POWERS.map((item) => [item.id, false]));
}

function setPageAccess(
  target: Record<string, PageAccess>,
  pageIds: string[],
  access: PageAccess,
) {
  for (const pageId of pageIds) {
    if (pageId in target) {
      target[pageId] = { ...access };
    }
  }
}

function setGroupAccess(
  target: Record<string, PageAccess>,
  groupId: string,
  access: PageAccess,
) {
  const group = findSidebarGroup(groupId);
  if (!group) return;
  setPageAccess(
    target,
    group.pages.map((page) => page.id),
    access,
  );
}

function setAllPages(access: PageAccess): Record<string, PageAccess> {
  const pages = emptyPagePermissions();
  setPageAccess(pages, getAllSidebarPermissionPageIds(), access);
  return pages;
}

function setPlatformFlags(
  target: Record<string, boolean>,
  ids: string[],
  enabled: boolean,
) {
  for (const id of ids) {
    if (id in target) target[id] = enabled;
  }
}

function setAllPlatformPowers(enabled: boolean): Record<string, boolean> {
  const powers = emptyPlatformPowers();
  setPlatformFlags(
    powers,
    PLATFORM_POWERS.map((item) => item.id),
    enabled,
  );
  return powers;
}

/** Sensible starting defaults by canonical role key. */
export function getDefaultRolePermissions(roleKey: string): RolePermissionsConfig {
  if (roleKey === "super-admin") {
    return {
      isFullAccess: true,
      pagePermissions: emptyPagePermissions(),
      platformPowers: setAllPlatformPowers(true),
    };
  }

  const pagePermissions = emptyPagePermissions();
  const platformPowers = emptyPlatformPowers();

  if (roleKey === "admin") {
    return {
      isFullAccess: false,
      pagePermissions: setAllPages({ view: true, edit: true }),
      platformPowers: setAllPlatformPowers(true),
    };
  }

  if (roleKey === "developer") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "development", { view: true, edit: true });
    setGroupAccess(pagePermissions, "admin", { view: true, edit: false });
    setPlatformFlags(platformPowers, ["view-audit-logs"], true);
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "support" || roleKey === "tech-support") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "messaging", { view: true, edit: true });
    setGroupAccess(pagePermissions, "accounts", { view: true, edit: false });
    setPageAccess(pagePermissions, ["employees"], { view: true, edit: false });
    setPlatformFlags(platformPowers, ["view-audit-logs"], true);
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "licensing") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "licensing", { view: true, edit: true });
    setGroupAccess(pagePermissions, "music", { view: true, edit: false });
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "music-director") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "music", { view: true, edit: true });
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "voice-over") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "messaging", { view: true, edit: true });
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "account-manager") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "accounts", { view: true, edit: true });
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "operations") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "accounts", { view: true, edit: false });
    setGroupAccess(pagePermissions, "inventory", { view: true, edit: true });
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "human-resources") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setGroupAccess(pagePermissions, "admin", { view: true, edit: true });
    setPlatformFlags(
      platformPowers,
      ["manage-employee-roles", "manage-employee-departments"],
      true,
    );
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  if (roleKey === "finance") {
    setGroupAccess(pagePermissions, "dashboard", { view: true, edit: false });
    setPageAccess(pagePermissions, ["transactions", "clients"], {
      view: true,
      edit: true,
    });
    setPlatformFlags(platformPowers, ["export-data"], true);
    return { isFullAccess: false, pagePermissions, platformPowers };
  }

  setPageAccess(pagePermissions, ["dashboard"], { view: true, edit: false });
  return { isFullAccess: false, pagePermissions, platformPowers };
}

export function normalizeRolePermissions(
  raw: Partial<RolePermissionsConfig> & {
    specialPermissions?: Record<string, boolean>;
  } | null | undefined,
  roleKey: string,
): RolePermissionsConfig {
  const defaults = getDefaultRolePermissions(roleKey);
  if (!raw) return defaults;

  const pagePermissions = emptyPagePermissions();
  for (const pageId of getAllSidebarPermissionPageIds()) {
    const saved = raw.pagePermissions?.[pageId];
    pagePermissions[pageId] = {
      view: saved?.view === true,
      edit: saved?.edit === true,
    };
  }

  const rawPowers = raw.platformPowers || raw.specialPermissions || {};
  const platformPowers = emptyPlatformPowers();
  for (const item of PLATFORM_POWERS) {
    platformPowers[item.id] = rawPowers[item.id] === true;
  }

  return {
    isFullAccess: raw.isFullAccess === true || defaults.isFullAccess,
    pagePermissions,
    platformPowers,
  };
}

export function permissionsFromApiRole(role: ApiEmployeeRole): RolePermissionsConfig {
  if (
    role.isFullAccess == null &&
    role.pagePermissions == null &&
    role.platformPowers == null
  ) {
    return getDefaultRolePermissions(role.roleKey);
  }

  return normalizeRolePermissions(
    {
      isFullAccess: role.isFullAccess,
      pagePermissions: role.pagePermissions,
      platformPowers: role.platformPowers,
    },
    role.roleKey,
  );
}

export function permissionsToApiPayload(permissions: RolePermissionsConfig): {
  isFullAccess: boolean;
  pagePermissions: Record<string, PageAccess>;
  platformPowers: Record<string, boolean>;
} {
  return {
    isFullAccess: permissions.isFullAccess,
    pagePermissions: permissions.pagePermissions,
    platformPowers: permissions.platformPowers,
  };
}

/** Resolve session permissions from /sa/super-admin/me, with role-key defaults as fallback. */
export function resolveSessionRolePermissions(
  identity: {
    rolePermissions?: ResolvedRolePermissions | null;
    employeeRoleKey?: string | null;
  } | null | undefined,
): ResolvedRolePermissions | null {
  if (!identity) return null;
  if (identity.rolePermissions) {
    return identity.rolePermissions;
  }
  return getDefaultRolePermissions(identity.employeeRoleKey || "super-admin");
}

export function canViewPage(
  permissions: ResolvedRolePermissions | null | undefined,
  pageId: string,
): boolean {
  if (!permissions || !pageId) return false;
  if (permissions.isFullAccess) return true;
  return permissions.pagePermissions[pageId]?.view === true;
}

export function canEditPage(
  permissions: ResolvedRolePermissions | null | undefined,
  pageId: string,
): boolean {
  if (!permissions || !pageId) return false;
  if (permissions.isFullAccess) return true;
  return permissions.pagePermissions[pageId]?.edit === true;
}

export function hasPlatformPower(
  permissions: ResolvedRolePermissions | null | undefined,
  powerId: string,
): boolean {
  if (!permissions || !powerId) return false;
  if (permissions.isFullAccess) return true;
  return permissions.platformPowers[powerId] === true;
}

export function updatePagePermission(
  current: RolePermissionsConfig,
  pageId: string,
  field: keyof PageAccess,
  value: boolean,
): RolePermissionsConfig {
  const existing = current.pagePermissions[pageId] || { view: false, edit: false };
  const nextAccess: PageAccess = {
    ...existing,
    [field]: value,
  };
  if (field === "view" && !value) {
    nextAccess.edit = false;
  }
  if (field === "edit" && value) {
    nextAccess.view = true;
  }

  return {
    ...current,
    pagePermissions: {
      ...current.pagePermissions,
      [pageId]: nextAccess,
    },
  };
}

export interface GroupAccessAggregate {
  viewChecked: boolean;
  viewIndeterminate: boolean;
  editChecked: boolean;
  editIndeterminate: boolean;
}

export function getGroupPageIds(groupId: string): string[] {
  return findSidebarGroup(groupId)?.pages.map((page) => page.id) ?? [];
}

export function getGroupAccessAggregate(
  permissions: RolePermissionsConfig,
  groupId: string,
): GroupAccessAggregate {
  const pageIds = getGroupPageIds(groupId);
  if (pageIds.length === 0) {
    return {
      viewChecked: false,
      viewIndeterminate: false,
      editChecked: false,
      editIndeterminate: false,
    };
  }

  const accesses = pageIds.map(
    (pageId) => permissions.pagePermissions[pageId] || { view: false, edit: false },
  );
  const viewCount = accesses.filter((access) => access.view).length;
  const editCount = accesses.filter((access) => access.edit).length;
  const total = pageIds.length;

  return {
    viewChecked: viewCount === total,
    viewIndeterminate: viewCount > 0 && viewCount < total,
    editChecked: editCount === total,
    editIndeterminate: editCount > 0 && editCount < total,
  };
}

/** Apply View or Edit to every page in a sidebar group. */
export function updateGroupPermission(
  current: RolePermissionsConfig,
  groupId: string,
  field: keyof PageAccess,
  value: boolean,
): RolePermissionsConfig {
  let next = current;
  for (const pageId of getGroupPageIds(groupId)) {
    next = updatePagePermission(next, pageId, field, value);
  }
  return next;
}

export function updatePlatformPower(
  current: RolePermissionsConfig,
  powerId: string,
  enabled: boolean,
): RolePermissionsConfig {
  return {
    ...current,
    platformPowers: {
      ...current.platformPowers,
      [powerId]: enabled,
    },
  };
}

/** @deprecated Use updatePlatformPower */
export const updateSpecialPermission = updatePlatformPower;
